import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import * as URI from 'urijs';
import 'urijs/src/URITemplate';

import { AuthService } from '®shared/auth';
import { AppConfig } from '®shared/config';

export type ResponseBuilderFunc<T> = () => Observable<T>;
export type HttpHeadersWrapper = { headers: HttpHeaders };
export type HttpParamsWrapper = { params: HttpParams };

/**
 * Wrap `HttpClient` to provide shared V3 API and JSON:API characteristics.
 *
 * - URLs are prefixed with the AppConfig's API root (domain, version, tenant)
 * - Default headers, including authorization, are added to the request options
 * - Responses are deserialised.
 */
@Injectable()
export class TarlV3Api {
  constructor(
    private appConfig: AppConfig,
    private authService: AuthService,
    private httpClient: HttpClient,
  ) {}

  /**
   * Makes an HTTP POST request to a v3 API endpoint, adding default behaviours for v3 APIs:
   *
   * - URLs are prefixed with the AppConfig's API root (domain, version, tenant)
   * - Default headers, including authorization, are added to the request options
   *
   * @param {string} url - A route path, relative to the V3 API base route.
   * @param {any} body - Request body data.
   * @returns {Observable<HttpResponse<Object>>} - A response
   */
  post(url: string, body: any): Observable<HttpResponse<Object>> {
    const buildRequest = () =>
      this.httpClient.post(this.prefixUrl(url), body, {
        observe: 'response',
        ...this.getCommonHeaders(),
      });

    return buildRequest().pipe(
      catchError(this.handleAuthenticationFailure(buildRequest)),
    );
  }

  /**
   * Gets common headers for V3 API requests.
   *
   * @returns {HttpHeadersWrapper} - common headers
   */
  getCommonHeaders(): HttpHeadersWrapper {
    return { headers: this.getAllHeaders() };
  }

  /**
   * Construct a URL path using variable replacement.
   *
   * This follows the scheme of URI.expand, which replaces handlebars {}-enclosed variable
   * names with the values provided in a map of replacements.
   *
   * @param {string} pathTemplate - A path, including {}-enclosed variable names
   * @param {object} substitutions - Map of variable-name replacement values
   * @returns {string} - An absolute URL to a V3 API endpoint
   */
  path(
    pathTemplate: string,
    substitutions: { [id: string]: string | number | boolean },
  ): string {
    return URI.expand(pathTemplate, substitutions).toString();
  }

  /**
   * Construct a V3 API URL relative to the V3 API and tenant, given a path.
   *
   * @param {string?} path - A relative path to a V3 API endpoint.
   * @returns {string} - An absolute URL to a V3 API endpoint
   */
  prefixUrl(path?: string): string {
    if (!path || !path.trim()) {
      return this.getBaseUrl();
    } else if (this.isAbsoluteUrl(path)) {
      return path;
    } else {
      // get just the path components, filter duplicate slashes, and reconstruct
      const pathParts = new URI()
        .segment([])
        .segment(path)
        .segment();
      const nonEmptyParts = pathParts.filter(part => part.trim().length > 0);
      return new URI(this.getBaseUrl())
        .segment(nonEmptyParts.join('/'))
        .toString();
    }
  }

  /**
   * Test whether the provided URL is absolute, to the V3 API prefix.
   *
   * @param {string?} urlish - A string which may be absolute to a V3 API endpoint.
   * @returns {boolean} - true if an absolute V3 URL; false otherwise
   */
  isAbsoluteUrl(urlish: string): boolean {
    const url = new URI(urlish);
    return url.absoluteTo(this.getBaseUrl()).toString() === urlish;
  }

  /**
   * Get the base URL for all V3 API routes for the currently-configured tenant.
   *
   * @returns {string} - An absolute URL to a V3 API base endpoint (for tenant)
   */
  getBaseUrl() {
    return new URI(this.appConfig.apiEndpoint)
      .segment(this.appConfig.tenant.shortCode)
      .toString();
  }

  /**
   * Get default headers for V3 API requests.
   *
   * @returns {HttpHeaders} - Header values common to all V3 API requests
   */
  getAllHeaders(): HttpHeaders {
    const authHeaders = this.authService.getBearerHeader();
    const userHeaders = this.authService.getUserHeader();
    return new HttpHeaders({
      ...authHeaders,
      ...userHeaders,
    });
  }

  /**
   * Handle Persona authentication failure by attempting a single Persona reauthentication request.
   *
   * @param {ResponseBuilderFunc} buildRequest - function to rebuild the original request from current state
   * @return a function for handling authentication failure and reattempting the original request
   */
  handleAuthenticationFailure<T>(
    buildRequest: ResponseBuilderFunc<T>,
  ): (HttpErrorResponse) => Observable<T> {
    return (response: HttpErrorResponse): Observable<T> => {
      if (response instanceof HttpErrorResponse && response.status === 401) {
        return this.reauthenticateAndRetry(buildRequest);
      }
      throw response;
    };
  }

  /**
   * Performs Persona reauthentication and reattempts a request.
   *
   * @param  {ResponseBuilderFunc<T>}  buildRequest - a function to regenerate the original request
   * @return {Observable<T>} - an Observable that reauthenticates and resubmits request.
   */
  reauthenticateAndRetry<T>(
    buildRequest: ResponseBuilderFunc<T>,
  ): Observable<T> {
    return this.authService.reauthenticate().pipe(switchMap(buildRequest));
  }
}
